/**
 * Utility functions for handling standardized API responses
 *
 * All backend endpoints now return this format (matching backend ApiResponse<T>):
 * {
 *   statusCode: number,
 *   message: string,
 *   data?: any,
 *   error?: string,
 *   timestamp: string,
 *   timeTakenMs?: number,
 *   path?: string
 * }
 */

export interface ApiError {
  message: string;
  code?: string | number;
  retryAfterSeconds?: number;
  /** True for rejections that must never be toasted (e.g. the axios
   *  interceptor's silently-recovered MFA-window 403s). */
  silent?: boolean;
}

export interface StandardizedApiResponse<T = any> {
  statusCode: number;
  message: string;
  data?: T; // data might be null/undefined on error
  error?: string; // optional error property for error descriptions
  timestamp: string;
  timeTakenMs?: number; // time taken in milliseconds
  path?: string; // API endpoint path
}

export function extractApiData<T = any>(response: any): T {
  if (!response || typeof response !== 'object') {
    return response as T;
  }
  // Check for standardized backend response (statusCode + data)
  // Note: 'message' is optional for some endpoints (e.g., fax-access)
  if ('statusCode' in response && 'data' in response) {
    // Backend StandardizedApiResponse format
    let data = response.data;

    // Handle double-wrapping: some endpoints return {statusCode, data: {statusCode, data: {...}}}
    // where the inner object also has statusCode and data properties
    if (data && typeof data === 'object' && 'statusCode' in data && 'data' in data) {
      data = data.data;
    }

    // Check if data is wrapped in another layer (e.g., { success, data, message })
    if (data && typeof data === 'object' && 'data' in data && 'success' in data) {
      return data.data as T;
    }
    return data as T;
  }
  if ('data' in response) {
    return (response as { data: T }).data;
  }
  return response as T;
}

export function isApiResponseSuccessful(response: any): boolean {
  if (!response || typeof response !== 'object') {
    return false;
  }
  if ('statusCode' in response) {
    const sc = (response as { statusCode?: number }).statusCode;
    return typeof sc === 'number' && sc >= 200 && sc < 300;
  }
  return true;
}

export function normalizeApiError(err: any): ApiError {
  // The axios interceptor rejects silently-recovered MFA-window 403s as
  // Error('') with silent=true — the empty message is deliberate (the request
  // is retried once the MFA marker lands, and the user must not be
  // interrupted). Defaulting it to 'Unknown error' here re-created the toast
  // the interceptor was written to suppress (seen at every sign-in on the
  // mobile app, where the post-MFA boot burst races the marker). Keep the
  // message empty and carry the silence forward.
  if (err?.silent === true || err?.code === 'MFA_REQUIRED' || err?.error === 'MFA_REQUIRED') {
    return { message: '', code: err?.code ?? 'MFA_REQUIRED', silent: true };
  }

  // Axios-style error with response
  const status = err?.response?.status as number | undefined;
  const headers = err?.response?.headers as Record<string, unknown> | undefined;
  const data = err?.response?.data as any;

  // A request with no reply (timeout, offline) reaches here as axios's bare message string.
  const bare = typeof err === 'string' ? err : '';
  const message =
    (data && typeof data === 'object' && typeof data.message === 'string' && data.message) ||
    (typeof err?.message === 'string' && err.message) ||
    (/^timeout of \d+ms exceeded$/.test(bare) ? 'The request timed out. Please try again.' : bare) ||
    'Unknown error';

  // Retry-After header can be string seconds or HTTP-date
  let retryAfterSeconds: number | undefined;
  const retryAfter = (headers?.['retry-after'] || headers?.['Retry-After']) as string | undefined;
  if (retryAfter) {
    const asNum = Number(retryAfter);
    if (!Number.isNaN(asNum)) {
      retryAfterSeconds = asNum;
    } else {
      const dateMs = Date.parse(retryAfter);
      if (!Number.isNaN(dateMs)) {
        retryAfterSeconds = Math.max(0, Math.round((dateMs - Date.now()) / 1000));
      }
    }
  }

  const code = (data && (data.code as string | number | undefined)) ?? status;
  return { message, code, retryAfterSeconds };
}

export async function handleApiCall<T = any>(apiCall: () => Promise<any>): Promise<T> {
  try {
    const response = await apiCall();

    // IMPORTANT: The axios interceptor already unwraps response.data
    // So 'response' here is actually the backend's standardized response format:
    // { statusCode, message, data, timestamp, ... }
    // NOT the axios response format: { data: {...}, status: 200, ... }

    // Since axios interceptor already unwrapped, response IS the backend data
    const responseData = response;

    const data = extractApiData<T>(responseData);

    // Check success on the actual API response (response.data), not Axios response
    if (!isApiResponseSuccessful(responseData)) {
      const normalized = normalizeApiError({ response: { data: responseData } });
      const e = new Error(normalized.message) as Error & ApiError;
      e.code = normalized.code;
      e.retryAfterSeconds = normalized.retryAfterSeconds;
      e.silent = normalized.silent;
      throw e;
    }
    return data;
  } catch (error: any) {
    const normalized = normalizeApiError(error);
    const e = new Error(normalized.message) as Error & ApiError;
    e.code = normalized.code;
    e.retryAfterSeconds = normalized.retryAfterSeconds;
    e.silent = normalized.silent;
    throw e;
  }
}


/**
 * One rule for every service's handleError: should this failure pop a toast?
 *  - never for a silently-recovered MFA-window error (the interceptor refetches);
 *  - never for a 401 (the axios interceptor refreshes or signs out — a toast on
 *    top of that is noise);
 *  - never for a READ ("fetch …", "get …", "load …", "check …"): the screen that
 *    asked renders its own error state and react-query retries. On a phone with
 *    a flaky cell link, toasting every failed read is what "Failed to fetch
 *    appointments" popping over a list about to retry looked like (2026-09-04).
 * Writes (create / update / delete / send …) still toast — the user did that.
 */
export function shouldToastApiError(normalized: ApiError, operation: string): boolean {
  if (normalized.silent) return false;
  if (normalized.code === 401 || String(normalized.code) === '401') return false;
  if (/^(fetch|get|load|check|retrieve|search|list)\b/i.test(operation.trim())) return false;
  return true;
}
